import {ref, computed} from 'vue';
import type {Store} from '../store.ts';
import {useLayersStore} from '../stores/layers.ts';
import {useDiffToggleStore} from '../stores/diffToggle.ts';
import {saveDiffFilterStateImmediate} from '../diffFilterPersistence.ts';

/**
 * Composable that manages the "Custom diff settings applied" banner state
 * and the reset action. Keeps feature-specific logic out of App.vue.
 */
export function useDiffConfigBanner(store: Store) {
  const layersStore = useLayersStore();
  const diffToggleStore = useDiffToggleStore();
  const previewResetKey = ref(0);

  // Use actual component states (not stored config) to determine if custom settings are active.
  // This ensures the banner reflects what is actually visible on the current page.
  const hasCustomDiffConfig = computed(() => {
    // Check if diff toggle is not at default (using the Pinia toggle store)
    const isNonDefaultToggle = diffToggleStore.selectedKey !== 'full';

    // Check if any filter items are hidden (using the actual hidden IDs synced by DiffFilter)
    const hasHiddenFilters = store.diffFilterHiddenIds.value.length > 0;

    // Check if the user has explicitly changed layer visibility (not just file defaults).
    // layerOverrides is only populated when the user interacts with the layer toggle.
    // Only relevant when the current file is a PCB; ignore stale overrides on schematics.
    const activeFileType = store.hashToPathMap.value.get(store.activeHash.value)?.ty;
    const hasLayerOverrides = activeFileType === 'pcb-diff' && Object.keys(store.diffFilterConfig.layerOverrides.value).length > 0;

    return isNonDefaultToggle || hasHiddenFilters || hasLayerOverrides;
  });

  async function resetDiffConfig() {
    if (!store?.diffFilterConfig) return;
    // Reset stored config (used when components re-mount to read initial state)
    store.diffFilterConfig.diffToggle.value = 'full';
    store.diffFilterConfig.filterOverrides.value = {};
    store.diffFilterConfig.layerOverrides.value = {};
    // Reset actual component states
    diffToggleStore.select('full');
    store.diffFilterHiddenIds.value = [];
    // Restore layers to file-defined defaults (don't $reset() — that clears
    // the layer metadata, which removes entries from the dropdown).
    if (layersStore.hasLayers) {
      layersStore.resetToDefaults();
    }
    // Save to backend immediately (bypass debounce so hard-refresh sees the reset)
    try {
      await saveDiffFilterStateImmediate(store);
    } catch (err) {
      console.error('Failed to save reset state:', err);
    }
    // Force all preview components to remount by changing the key.
    // New component instances will read the reset store state.
    previewResetKey.value++;
  }

  return {hasCustomDiffConfig, resetDiffConfig, previewResetKey};
}
